<template>
    <div>
        <div v-if="items.total > 0" class="transfer-selection">
            <Requirement :trip="trip" :onScroll="onClose" />
            <Option
                v-for="item in items.available"
                :option="item"
                :onSelect="onSelect"
                :onAdjustPickupTime="onAdjustPickupTime"
                :key="item.Id"
                :trip="trip" />

            <Option
                v-for="item in items.unavailable"
                :option="item"
                :onAdjustPickupTime="onAdjustPickupTime"
                :key="item.Id"
                :trip="trip" />
        </div>

        <Notification v-else :title="t({ key: 'shared.no_alternatives' })" />
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, PropType } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { isEmpty } from 'lodash';
    import { TransferOption, TransferOptionItem } from '@/api/Transfer/transfer.interfaces';
    import { logStatsigCheckoutTransferAvailableOptions } from '@/tracking/statsig/checkout.functions';
    import {
        TransferSelectionItems,
        TransferTrip,
        TransferValidateType,
    } from '@/helpers/transfer/transfer.interfaces';
    import { validateTransferOptionConditions } from '@/helpers/transfer/transfer.conditions';
    import Option from './Option/Option.vue';
    import Notification from '@ui/components/Notification/Notification.vue';
    import Requirement from '@/components/checkoutPageV3/Sections/Addons/Transfer/Trips/Trip/Shared/Requirement.vue';

    export default defineComponent({
        name: 'TransferSelection',
        methods: { t },
        components: { Requirement, Option, Notification },
        props: {
            options: {
                type: Array as PropType<TransferOption[]>,
                default: () => [],
            },
            trip: {
                type: Object as PropType<TransferTrip>,
                required: true,
            },
            onSelect: { type: Function as PropType<(option: TransferOption) => void> },
            onAdjustPickupTime: { type: Function as PropType<() => void> },
            onClose: { type: Function, required: true },
        },
        setup(props) {
            onMounted(() => {
                if (!isEmpty(props.options)) {
                    logStatsigCheckoutTransferAvailableOptions(props.options);
                }
            });

            const items = computed<TransferSelectionItems>(() => {
                const available: TransferOptionItem[] = [];
                const unavailable: TransferOptionItem[] = [];

                if (!isEmpty(props.options)) {
                    props.options.forEach((option) => {
                        if (option.Duration !== null) {
                            const result = validateTransferOptionConditions(
                                props.trip,
                                option
                            );

                            if (result?.type === TransferValidateType.Invalid) {
                                unavailable.push({
                                    ...option,
                                    Validation: {
                                        Type: result.type,
                                        Message: result.message,
                                    },
                                });
                            } else {
                                available.push({
                                    ...option,
                                    Validation: result
                                        ? {
                                              Type: result.type,
                                              Message: result.message,
                                          }
                                        : undefined,
                                });
                            }
                        }
                    });
                }

                return {
                    available,
                    unavailable,
                    total: available.length + unavailable.length,
                };
            });

            return { items };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Selection.scss';
</style>
